import { useQuery } from "@tanstack/react-query";
import { abrirComoSuporte, chamar, empresaDeSuporte } from "@hugi/ui";

/**
 * O ACESSO DE SUPORTE, na tela (0067 do Cert4All).
 *
 * ## O que ele resolve
 *
 * O plano Profissional **não tem painel por desenho**, e até 06/09 não havia caminho nenhum para dar
 * manutenção numa conta dessas: o tenant do painel vem do `companyId` do `introspect`, e nem
 * `global_admin` troca de tenant ali. Sobravam virar membro da empresa da cliente, SQL na máquina,
 * ou uma sessão de agente.
 *
 * Agora o operador abre o painel **dela**, por concessão com prazo dada no Cert4All — e tudo o que
 * ele escrever sai marcado como `suporte:<id>` no rastro que a cliente lê.
 */
interface Acesso {
  readonly id: string;
  readonly companyId: string;
  readonly empresa: string;
  readonly motivo: string;
  readonly expiraEm: string;
}

interface Resposta {
  readonly acessos: readonly Acesso[];
  readonly atual: { readonly empresa: string; readonly motivo: string; readonly expiraEm: string } | null;
}

function useAcessosDeSuporte() {
  return useQuery({
    queryKey: ["suporte", "acessos"],
    queryFn: () => chamar<Resposta>("/api/painel/suporte/acessos"),
    staleTime: 60_000,
  });
}

/** "até 18:40 de hoje" é mais útil que uma data ISO para quem está com o prazo correndo. */
function ate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }).format(d);
}

/**
 * A FAIXA — e ela é deliberadamente difícil de ignorar.
 *
 * ⚠️ **Não é um selo discreto no canto**, e a razão é o modo de falha: o operador esquece que está
 * na conta de um cliente, edita achando que é a dele, e descobre pelo cliente. A faixa ocupa a
 * largura toda, usa a cor de atenção, diz o nome da empresa e traz o botão de sair — porque sair
 * tem de ser mais fácil que continuar.
 */
export function FaixaDeSuporte() {
  const q = useAcessosDeSuporte();
  const atual = q.data?.atual ?? null;
  if (!atual) return null;
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-[color:var(--atencao-clara)] px-4 py-1.5 text-[13px] text-[color:var(--atencao-texto)]"
    >
      <span className="font-semibold">Acesso de suporte</span>
      <span>
        Você está vendo a conta de <strong>{atual.empresa}</strong>. Tudo o que você fizer fica
        registrado como acesso da Cert4All.
      </span>
      <span className="opacity-80">até {ate(atual.expiraEm)}</span>
      <button
        type="button"
        className="botao ml-auto"
        onClick={() => {
          abrirComoSuporte(null);
          window.location.reload();
        }}
      >
        Voltar para a minha conta
      </button>
    </div>
  );
}

/**
 * O SELETOR, no cabeçalho.
 *
 * ⚠️ Ele **só existe quando há acesso vivo** — quem não tem concessão não vê nada, e não é um
 * controle desabilitado: um seletor vazio no cabeçalho de todo mundo seria a promessa de um poder
 * que a pessoa não tem, no lugar mais visível do produto.
 */
export function SeletorDeSuporte() {
  const q = useAcessosDeSuporte();
  const acessos = q.data?.acessos ?? [];
  const atual = empresaDeSuporte();
  if (acessos.length === 0 && !atual) return null;

  return (
    <label className="flex items-center gap-2">
      <span className="sr-only">Abrir a conta de um cliente por acesso de suporte</span>
      <select
        className="campo max-w-full text-xs"
        value={atual ?? ""}
        onChange={(e) => {
          abrirComoSuporte(e.target.value || null);
          /*
            ⚠️ RECARREGA, e não é preguiça: a troca muda o tenant de TODA consulta já em cache —
            conversas, catálogos, agente. Invalidar seletivamente deixaria a tela misturando dados
            de duas empresas enquanto as consultas voltam, e é justamente a mistura que este
            mecanismo não pode produzir.
          */
          window.location.reload();
        }}
      >
        <option value="">Minha conta</option>
        {acessos.map((a) => (
          <option key={a.id} value={a.companyId}>
            {a.empresa}
          </option>
        ))}
      </select>
    </label>
  );
}
